import {
    markNotificationShownAction,
    saveFormResponsesThunk
} from 'actions/formsInvitation/FormsInvitationActions';
import { ClientInfoCard } from 'components/ClientForm/ClientInfoCard/ClientInfoCard';
import { ContinueButton } from 'components/ClientForm/ContinueButton/ContinueButton';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import {
    firstVisibleFormId,
    PendingForm,
    PendingFormList
} from 'components/ClientForm/PendingFormList/PendingFormList';
import { Header } from 'components/Layout/Header/Header';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getFormsInvitationAnswers,
    getFormsInvitationData,
    getNotifyAddQuestionShown
} from 'reducers/formsInvitation/FormsInvitationSelectors';
import { getMarketplace } from 'reducers/marketplace/MarketplaceSelectors';
import classes from './ClientFormPage.module.scss';
import { FormStatus } from 'types/FormsInvitation';
import ErrorList from 'components/Form/ErrorList/ErrorList';
import { AlertScreen, AlertVariant } from 'types/Alert';
import { addAlertThunk } from 'actions/alerts/AlertsActions';
import { AlertSuccess } from '../AlertSuccess/AlertSuccess';

interface ClientFormPageProps {
    currentStep: number;
    totalSteps: number;
    notifyAddQuestion: boolean;

    // Client info props
    clientName: string;
    clientPhone: string;
    clientEmail: string;

    // Forms props
    forms: PendingForm[];

    // Flow control
    isLastStep?: boolean;

    // Handlers
    onContinue: () => void;
}

export const ClientFormPage: React.FC<ClientFormPageProps> = props => {
    const dispatch = useDispatch();
    const marketplace = useSelector(getMarketplace);
    const invitationData = useSelector(getFormsInvitationData);
    const reduxAnswers = useSelector(getFormsInvitationAnswers);
    const notifyAddQuestionShown = useSelector(getNotifyAddQuestionShown);
    const progress = (props.currentStep / props.totalSteps) * 100;

    const [expandedFormId, setExpandedFormId] = React.useState<number | null>(() =>
        firstVisibleFormId(props.forms)
    );
    const [formAnswers, setFormAnswers] = React.useState<{
        [formId: number]: { [questionId: number]: string | string[] };
    }>({});

    // Initialize formAnswers from Redux (pre-filled answers from backend)
    React.useEffect(() => {
        if (reduxAnswers && Object.keys(formAnswers).length === 0) {
            // Filter only customer form answers
            const customerFormIds = props.forms.map(f => f.id);
            const customerAnswers: typeof formAnswers = {};

            customerFormIds.forEach(formId => {
                if (reduxAnswers[formId]) {
                    customerAnswers[formId] = reduxAnswers[formId];
                }
            });

            setFormAnswers(customerAnswers);
        }
    }, [reduxAnswers, props.forms, formAnswers]);

    React.useEffect(() => {
        if (props.notifyAddQuestion && !notifyAddQuestionShown) {
            dispatch(
                addAlertThunk(
                    <AlertSuccess message="New questions have been added. Please review and answer them before submitting." />,
                    AlertScreen.FormInvitationCustomer,
                    AlertVariant.Success
                )
            );
            dispatch(markNotificationShownAction('addQuestion'));
        }
    }, [props.notifyAddQuestion, notifyAddQuestionShown, dispatch]);

    const handleToggle = (formId: number) => {
        setExpandedFormId(prev => (prev === formId ? null : formId));
    };

    const handleSave = (formId: number, answers: { [questionId: number]: string | string[] }) => {
        // Update local state immediately for UI
        setFormAnswers(prev => ({
            ...prev,
            [formId]: answers
        }));

        // Save to backend (create or update)
        if (invitationData) {
            dispatch(
                saveFormResponsesThunk(formId, answers, 'Customer', invitationData.customer.id)
            );
        }
    };

    const isFormComplete = (form: PendingForm): boolean => {
        const requiredQuestions = form.questions.filter(q => q.required);
        if (requiredQuestions.length === 0) return true;

        const answers = formAnswers[form.id] || {};
        return requiredQuestions.every(q => {
            const answer = answers[q.id];
            if (Array.isArray(answer)) {
                return answer.length > 0;
            }
            return answer && answer.toString().trim().length > 0;
        });
    };

    const formsWithStatus: PendingForm[] = props.forms.map(form => ({
        ...form,
        status: isFormComplete(form) ? FormStatus.Completed : FormStatus.Pending
    }));

    const allFormsCompleted = formsWithStatus.every(form => form.status === FormStatus.Completed);

    return (
        <TwoColumnsLayout header={<Header logo={marketplace.logo} progress={progress} />}>
            <LeftColumn className={classes.leftColumn}>
                <ClientInfoCard
                    clientName={props.clientName}
                    clientPhone={props.clientPhone}
                    clientEmail={props.clientEmail}
                />
            </LeftColumn>

            <RightColumn
                className={classes.rightColumn}
                contentClassName={classes.rightColumnContent}
                footerContentClassName={classes.rightColumnFooterContent}
                footer={
                    <ContinueButton
                        onClick={props.onContinue}
                        disabled={!allFormsCompleted}
                        isLastStep={props.isLastStep}
                    />
                }
            >
                <>
                    <MarketplaceInfo
                        hideLogo
                        marketplace={marketplace}
                        className={classes.marketplaceInfo}
                    />

                    <ErrorList screen={AlertScreen.FormInvitationCustomer} className="mt-5 mb-5" />

                    <PendingFormList
                        forms={formsWithStatus}
                        expandedFormId={expandedFormId}
                        answers={formAnswers}
                        onToggle={handleToggle}
                        onSave={handleSave}
                    />
                </>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
